import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTryIt } from '@/store/tryIt';
import { generateSnippet, type SnippetLanguage } from '@/lib/codeGen';
import { CodeBlock } from './CodeBlock';
import { JsonTree } from './JsonTree';
import { AskAiBar } from '@/components/ai/AskAiBar';
import type { ShikiLang } from '@/lib/shiki';
import { cn } from '@/lib/cn';

const LANGS: { id: SnippetLanguage; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'js', label: 'JS' },
  { id: 'python', label: 'Python' },
  { id: 'php', label: 'PHP' },
];

/**
 * Right panel: live code snippets + response viewer.
 *
 * Both panes observe the tryIt store so they update as the user fills the
 * form. Empty-state copy keeps placeholders friendly when no endpoint is
 * selected yet.
 */
/**
 * Right column — terdiri dari 3 collapsible pane:
 *   1. Snippet (code generator per lang)   — default expanded
 *   2. Response (JSON viewer of last fetch) — default expanded, dapat flex-1
 *      shrink ke kosong kalau belum ada response
 *   3. Ask AI                                — default expanded, ambil sisa
 *      vertical space
 *
 * 3 pane vertically stacked, dipisah border 2px brutal. User dapat collapse
 * snippet/response biar AI chat punya lebih banyak vertical room.
 */
export function RightPanel() {
  return (
    <aside className="w-[480px] shrink-0 hidden xl:flex flex-col border-l-[3px] border-border-strong bg-base">
      <SnippetPane />
      <ResponsePane />
      <AskAiBar variant="right" />
    </aside>
  );
}


function SnippetPane() {
  const state = useTryIt();
  const [lang, setLang] = useState<SnippetLanguage>('curl');
  const [copied, setCopied] = useState(false);

  const snippet = state.endpoint
    ? generateSnippet(lang, state.endpoint, state)
    : '# Pilih endpoint untuk lihat snippet';

  const copy = async () => {
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="border-b-2 border-border-strong">
      <div className="px-3 py-2.5 border-b-2 border-border-strong flex items-center gap-1.5">
        {LANGS.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => setLang(l.id)}
            className={cn(
              'px-3 h-7 rounded-pill text-2xs font-mono font-bold transition-all border-2',
              lang === l.id
                ? 'bg-method-post border-border-strong shadow-brutal-sm text-fg'
                : 'border-transparent text-fg-tertiary hover:text-fg hover:border-border-strong hover:bg-elevated'
            )}
          >
            {l.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={copy}
          className="w-7 h-7 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all flex items-center justify-center"
          title="Copy snippet"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      {/* max-h compressed dari 72 (288px) → 44 (176px) supaya AI chat di
          bawah ada lebih banyak vertical space. Snippet biasanya pendek
          (3-8 baris cURL), gak butuh banyak room. */}
      <div className="bg-elevated overflow-x-auto max-h-44">
        <CodeBlock
          code={snippet}
          language={shikiLang(lang)}
          className="[&_pre]:!bg-transparent [&_pre]:px-4 [&_pre]:py-3 [&_pre]:m-0 [&_code]:font-mono [&_code]:text-2xs [&_code]:leading-relaxed"
        />
      </div>
    </div>
  );
}

function shikiLang(snippetLang: SnippetLanguage): ShikiLang {
  switch (snippetLang) {
    case 'curl':
      return 'bash';
    case 'js':
      return 'javascript';
    case 'python':
      return 'python';
    case 'php':
      return 'php';
  }
}

function ResponsePane() {
  const state = useTryIt();
  const result = state.result;

  return (
    // max-h diset supaya kalau response panjang (besar JSON), gak push AI
    // chat keluar viewport. User scroll within response container instead.
    <div className="flex flex-col min-h-0 max-h-[40vh] border-b-2 border-border-strong">
      <div className="px-4 py-2.5 border-b-2 border-border-strong flex items-center justify-between shrink-0 bg-base">
        <h3 className="text-sm font-display font-bold uppercase tracking-wider">Response</h3>
        {result && (
          <div className="flex items-center gap-2 text-2xs font-mono font-bold">
            <StatusBadge status={result.status} ok={result.ok} />
            <span className="text-fg-tertiary">{result.durationMs.toFixed(0)}ms</span>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto bg-elevated">
        {!result && !state.isLoading && (
          <div className="px-4 py-4 font-mono text-xs text-fg-tertiary">
            <span>{'{'}</span>
            <br />
            <span>{'  "info": "Klik Send untuk lihat response di sini"'}</span>
            <br />
            <span>{'}'}</span>
          </div>
        )}
        {state.isLoading && (
          <div className="px-4 py-4 text-2xs font-mono text-fg-tertiary">
            <span className="inline-block animate-pulse">Loading...</span>
          </div>
        )}
        {result && (
          <div className="px-4 py-3">
            {typeof result.data === 'object' && result.data !== null ? (
              <JsonTree value={result.data} />
            ) : (
              <pre className="font-mono text-2xs text-fg-secondary leading-relaxed">
                <code>{formatResponse(result.data)}</code>
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, ok }: { status: number; ok: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-6 rounded-pill border-2 border-border-strong font-bold text-2xs',
        ok ? 'bg-method-get' : 'bg-method-delete'
      )}
    >
      {status}
    </span>
  );
}

function formatResponse(data: unknown): string {
  if (data === null || data === undefined) return '(no body)';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}
